import { HeaderNavIcons } from 'ui/icons/headerNavigation/constants';

/**
 * Supported navbar variants
 */
export type NavbarVariant = 'default' | 'nova';

/** Additional fields that determine link visibility outside of permissions and feature flags */
export enum NAVBAR_PROJECT_SETTINGS {
  /** Require org admin level to view certain links instead of rbacPermissions */
  orgAdmin = 'orgAdmin',
  /** Show links related to anon tracking based on current project in addition to FF check */
  enableAnonUserTracking = 'enableAnonUserTracking',
}

export type NavbarProjectSettingsType = typeof NAVBAR_PROJECT_SETTINGS;
export type NavbarProjectSettingsKeys = keyof NavbarProjectSettingsType;

export type NavbarProjectSettings = {
  [NAVBAR_PROJECT_SETTINGS.orgAdmin]?: boolean;
  [NAVBAR_PROJECT_SETTINGS.enableAnonUserTracking]?: boolean;
};

/**
 * Feature flag name
 */
type NavbarOptionFeatureFlagName = string;

/**
 * Feature flag config
 */
export type NavbarOptionFeatureFlagConfig = {
  name: NavbarOptionFeatureFlagName;
  disabled?: boolean;
};

/**
 * Feature flag
 */
export type NavbarOptionFeatureFlag = NavbarOptionFeatureFlagName | NavbarOptionFeatureFlagConfig;

/**
 * Copied from `src/core/navbar/constants` with some modifications
 * - NavbarOption removes icon: string, and adds Icon: ReactNode
 * - Consider a way to retrieve icon with key (ie. icon: 'Custom', <Icon name='Custom' />)
 */

export type NavbarOption = {
  /** The title text of the dropdown option */
  title: string;
  divider?: boolean;
  /**
   * The link the dropdown option navigates to (possibly absent if {@link NavbarOption.linksSubsection} is true)
   * function used for dynamic routes
   */
  href?: string;
  hideFromProspect?: boolean;
  /** name of icon to display */
  icon?: HeaderNavIcons;
  /** Array of links to be listed on the subsection
   * OR
   * Links that are not accessible in the navbar (if {@link NavbarOption.linksSubsection} is false) but belong to the
   * current navbar option. The navbar item will be active when the link is visited.
   */
  links?: NavbarOptionLink[];
  /** When true, there will be a subsection for the links provided in the links param */
  linksSubsection?: boolean;
  /** Subtext shown with the dropdown object */
  pageDescription?: string;
  /** Flag that when true, will trigger a full page refresh on page change. This should be true for all links to external sites (like help docs) */
  isExternal?: boolean;
  /** The RBAC permission needed, if any, to view the page */
  rbacPermission?: number | number[];
  /**
   * Feature flags that control navbar option visibility
   * - Strings: navbar option is visible when flag is active
   * - Objects: navbar option visibility controlled by flag state and disabled property
   */
  featureFlags?: NavbarOptionFeatureFlag[];
  /** Array of keys that should map to an object with boolean value */
  projectSettings?: NavbarProjectSettingsKeys[];
};

export type NavbarOptionLink = {
  isExternal?: NavbarOption['isExternal'];
  href: string;
  label: string;
  rbacPermission?: number | number[];
  featureFlags?: NavbarOptionFeatureFlag[];
  /** If true, at least one of the feature flags is required to view the page. If false/undefined, default behavior: all flags are required to view the page. */
  requireOnlyOneFlag?: boolean;
};

export type NavbarDropdownGroup = {
  /**
   * The label of the dropdown that is displayed in the global nav. If the {@link NavbarDropdownGroup.headerIcon} is true, then this will
   * be the icon name displayed in the global nav
   */
  header: string;
  /**
   * Flag that represents if the dropdown label should be an icon or not. If true, it will use the icon defined
   * in {@link NavbarDropdownGroup.header}
   */
  dropdownOptions: NavbarOption[];
  headerIcon?: boolean;
  /**
   * Flag that when true, will only render a simple link for each item in {@link NavbarDropdownGroup.dropdownOptions}
   */
  simpleLinks?: boolean;
  /** Flag that when true, will open all links in a new tab */
  shouldOpenInNewTab?: boolean;
};
